import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  Box,
  Radio,
  RadioGroup,
  FormControlLabel,
  Tooltip,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { useTranslation } from 'react-i18next';

import type { PollyLanguage } from '@shared/interfaces/polly';
import { languageFlags, languageLabels } from '../constants';
import { useModalTheme } from '@shared/components/AssistantEditorModals/context';
import {
  modalThemeTokens,
  buildModalCssVars,
} from '@shared/components/AssistantEditorModals/theme';

type TranslateChoice = 'overwrite' | 'empty';

interface TranslateConfirmDialogProps {
  open: boolean;
  // Languages in the requested set that already have a translation — replaced by
  // the "Overwrite all" option.
  existing: PollyLanguage[];
  // Languages in the requested set with no translation yet — the target of the
  // "Only empty" option.
  empty: PollyLanguage[];
  // Every language the "Overwrite all" option will translate — existing + empty,
  // in request order.
  targetLangs: PollyLanguage[];
  // Whether this confirm was triggered by "Auto-translate all" or a manually
  // picked language selection — changes the "Overwrite all" option's copy.
  origin: 'all' | 'picked';
  isTranslating: boolean;
  onOverwriteAll: () => void;
  onOnlyEmpty: () => void;
  onClose: () => void;
}

const actionButtonSx = {
  textTransform: 'none',
  borderRadius: 2,
  py: 1,
  px: 2,
} as const;

const OptionInfoTooltip = ({
  title,
  testId,
}: {
  title: string;
  testId: string;
}) => (
  <Tooltip title={title} placement="top" arrow>
    <InfoOutlinedIcon
      data-testid={testId}
      sx={{
        fontSize: 15,
        color: 'var(--am-icon-accent)',
        opacity: 0.75,
        cursor: 'default',
      }}
      onClick={(e) => e.stopPropagation()}
    />
  </Tooltip>
);

const LanguageList = ({
  langs,
  overwrittenLangs,
  isDark,
  warningBg,
}: {
  langs: PollyLanguage[];
  overwrittenLangs?: PollyLanguage[];
  isDark: boolean;
  warningBg: string;
}) => {
  const { t } = useTranslation('common');
  const overwrittenSet = new Set(overwrittenLangs ?? []);
  const sortedLangs = [...langs].sort((a, b) =>
    languageLabels[a].localeCompare(languageLabels[b]),
  );

  return (
    <Box
      component="ul"
      sx={{
        mt: 0.5,
        mb: 0,
        pl: 3,
        '& li': {
          display: 'flex',
          alignItems: 'center',
          fontSize: 15,
          lineHeight: 1.9,
          color: 'var(--am-text-primary)',
        },
      }}
    >
      {sortedLangs.map((lang) => (
        <li key={lang}>
          <span style={{ marginRight: 6 }}>{languageFlags[lang]}</span>
          {languageLabels[lang]}
          {overwrittenSet.has(lang) && (
            <Box
              component="span"
              sx={{
                ml: 1,
                fontSize: 11,
                fontWeight: 700,
                color: 'var(--am-warning-color)',
                backgroundColor: isDark
                  ? warningBg
                  : (theme) => alpha(theme.palette.warning.main, 0.12),
                px: 0.75,
                borderRadius: '3px',
                lineHeight: 1.8,
              }}
            >
              {t(
                'assistant.scriptDialog.translateOverwriteBadge',
                'Will be overwritten',
              )}
            </Box>
          )}
        </li>
      ))}
    </Box>
  );
};

const TranslateConfirmDialog = ({
  open,
  existing,
  empty,
  targetLangs,
  origin,
  isTranslating,
  onOverwriteAll,
  onOnlyEmpty,
  onClose,
}: TranslateConfirmDialogProps) => {
  const { t } = useTranslation('common');
  const modalTheme = useModalTheme();
  const tokens = modalThemeTokens[modalTheme];
  const cssVars = buildModalCssVars(tokens);
  const isDark = modalTheme === 'dark';
  // With no empty languages there is only one meaningful action (overwrite), so
  // we drop the radios and show a plain overwrite warning instead.
  const hasEmpty = empty.length > 0;

  const [choice, setChoice] = useState<TranslateChoice>('empty');

  // The dialog stays mounted between opens, so reset the default selection each
  // time it opens.
  useEffect(() => {
    if (open) {
      setChoice('empty');
    }
  }, [open]);

  const handleConfirm = () => {
    if (!hasEmpty || choice === 'overwrite') {
      onOverwriteAll();
    } else {
      onOnlyEmpty();
    }
  };

  const getOptionBorderColor = (selected: boolean) => {
    if (isDark) {
      return selected ? tokens.accentText : tokens.border;
    }

    return selected ? 'primary.main' : 'divider';
  };

  const optionCardSx = (selected: boolean) => ({
    background: isDark ? 'var(--am-surface)' : 'transparent',
    border: '1px solid',
    borderColor: getOptionBorderColor(selected),
    borderRadius: 2,
    p: 1.5,
    mt: 1.5,
    cursor: 'pointer',
    transition: 'border-color 0.15s',
    '& .MuiFormControlLabel-root': {
      alignItems: 'flex-start',
      m: 0,
    },
  });

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          p: 0,
          ...(cssVars as any),
          '& .MuiRadio-root.Mui-checked': {
            color: 'var(--am-icon-accent)',
          },
          // MUI's unchecked Radio defaults to theme.palette.action.active (an
          // MUI JS-theme shorthand) — in light mode that's already the right
          // dark gray, but in dark mode it stays that same light-mode black
          // regardless of our --am-* tokens, reading as invisible. Same
          // pitfall as InterruptSection's custom radio ring, which uses
          // var(--am-border-mid) instead — but only override for dark so
          // light keeps MUI's real default.
          ...(isDark && {
            '& .MuiRadio-root:not(.Mui-checked)': {
              color: 'var(--am-border-mid)',
            },
          }),
          ...(isDark && {
            background:
              'linear-gradient(135deg, rgba(28, 28, 34, 0.88) 0%, rgba(16, 16, 20, 0.88) 100%)',
            backdropFilter: 'blur(18px)',
            WebkitBackdropFilter: 'blur(18px)',
            border: '1px solid rgba(255, 255, 255, 0.08)',
            boxShadow: '0 18px 50px rgba(0, 0, 0, 0.45)',
            color: tokens.textPrimary,
            '& .MuiDialogTitle-root': { color: tokens.textPrimary },
            '& .MuiTypography-root': { color: tokens.textPrimary },
            '& .MuiButton-outlined': {
              color: tokens.textPrimary,
              borderColor: tokens.cancelBorder,
              '&:hover': { borderColor: tokens.cancelHoverBorder },
            },
            '& .MuiButton-containedPrimary': {
              backgroundColor: tokens.primaryBtnBg,
              color: 'rgb(226, 228, 237)',
              '&:hover': { backgroundColor: tokens.primaryBtnHoverBg },
              '&:disabled': {
                backgroundColor: tokens.borderMid,
                color: tokens.textSecondary,
              },
            },
          }),
        },
      }}
    >
      <DialogTitle sx={{ fontWeight: 700, fontSize: 17, pb: 0 }}>
        {t(
          'assistant.scriptDialog.translateConfirmTitle',
          'Existing translations found',
        )}
      </DialogTitle>
      <DialogContent sx={{ padding: '12px 24px !important' }}>
        {hasEmpty ? (
          <>
            <DialogContentText
              sx={{ color: 'var(--am-text-primary)', fontSize: 15 }}
            >
              {t(
                'assistant.scriptDialog.translateConfirmBody',
                'Some languages already have translations. Choose what to translate:',
              )}
            </DialogContentText>

            <RadioGroup
              value={choice}
              onChange={(e) => setChoice(e.target.value as TranslateChoice)}
            >
              <Box
                data-testid="translate-option-overwrite"
                sx={optionCardSx(choice === 'overwrite')}
                onClick={() => setChoice('overwrite')}
              >
                <FormControlLabel
                  value="overwrite"
                  control={<Radio sx={{ pt: 0 }} />}
                  label={
                    <Box>
                      <Box
                        sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
                      >
                        <Typography
                          sx={{
                            fontSize: 15,
                            fontWeight: 600,
                            color: 'var(--am-text-primary)',
                          }}
                        >
                          {origin === 'picked'
                            ? t(
                                'assistant.scriptDialog.translateOverwritePickedOption',
                                'Translate picked languages, overwriting existing translations',
                              )
                            : t(
                                'assistant.scriptDialog.translateOverwriteOption',
                                'Translate all, overwriting existing translations',
                              )}
                        </Typography>
                        <OptionInfoTooltip
                          testId="translate-overwrite-info-icon"
                          title={t(
                            'assistant.scriptDialog.translateOverwriteInfoTooltip',
                            "Translates the whole script — every step, for every listed language — and replaces any existing translation, including ones you've edited by hand.",
                          )}
                        />
                      </Box>
                      <LanguageList
                        langs={targetLangs}
                        overwrittenLangs={existing}
                        isDark={isDark}
                        warningBg={tokens.warningBg}
                      />
                    </Box>
                  }
                />
              </Box>

              <Box
                data-testid="translate-option-empty"
                sx={optionCardSx(choice === 'empty')}
                onClick={() => setChoice('empty')}
              >
                <FormControlLabel
                  value="empty"
                  control={<Radio sx={{ pt: 0 }} />}
                  label={
                    <Box>
                      <Box
                        sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
                      >
                        <Typography
                          sx={{
                            fontSize: 15,
                            fontWeight: 600,
                            color: 'var(--am-text-primary)',
                          }}
                        >
                          {t(
                            'assistant.scriptDialog.translateEmptyOption',
                            'Only translate empty languages',
                          )}
                        </Typography>
                        <OptionInfoTooltip
                          testId="translate-empty-info-icon"
                          title={t(
                            'assistant.scriptDialog.translateEmptyInfoTooltip',
                            'Translates the whole script, but only writes a translation where one is missing. Steps and languages that already have a translation are left untouched.',
                          )}
                        />
                      </Box>
                      <LanguageList
                        langs={empty}
                        isDark={isDark}
                        warningBg={tokens.warningBg}
                      />
                    </Box>
                  }
                />
              </Box>
            </RadioGroup>
          </>
        ) : (
          <>
            <DialogContentText
              sx={{
                color: 'var(--am-text-primary)',
                fontSize: 15,
                fontWeight: 500,
              }}
            >
              {t(
                'assistant.scriptDialog.translateOverwriteWarning',
                'Are you sure you want to overwrite these existing translations?',
              )}
            </DialogContentText>
            <LanguageList
              langs={existing}
              isDark={isDark}
              warningBg={tokens.warningBg}
            />
          </>
        )}
      </DialogContent>
      <DialogActions
        sx={{
          px: 3,
          pb: 2,
          pt: 2.5,
          gap: 1,
          justifyContent: 'space-between',
        }}
      >
        <Button
          variant="outlined"
          onClick={onClose}
          disabled={isTranslating}
          sx={actionButtonSx}
        >
          {t('assistant.scriptDialog.cancel', 'Cancel')}
        </Button>
        <Button
          variant="contained"
          color={hasEmpty ? 'primary' : 'error'}
          onClick={handleConfirm}
          disabled={isTranslating}
          sx={actionButtonSx}
        >
          {hasEmpty
            ? t('assistant.scriptDialog.translate', 'Translate')
            : t('assistant.scriptDialog.translateOverwriteShort', 'Overwrite')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default TranslateConfirmDialog;
